import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  docSource,
  formatBytes,
  listDocumentsForJob,
  typeChipColor,
  type DocumentSummary,
} from '../lib/documents';

export function Documents() {
  const { jobId } = useParams<{ jobId: string }>();
  const [docs, setDocs] = useState<DocumentSummary[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listDocumentsForJob(jobId);
        if (!cancelled) setDocs(list);
      } catch (e) {
        if (!cancelled) setErr((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  return (
    <div className="mx-auto max-w-2xl p-4">
      <Link to={`/job/${jobId}`} className="text-sm text-gray-600 underline">
        ‹ Job
      </Link>
      <h1 className="mt-2 text-xl font-bold">Documents</h1>

      {err && (
        <div className="mt-4 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {err}
        </div>
      )}
      {!docs && !err && (
        <div className="mt-4 text-sm text-gray-500">Loading…</div>
      )}
      {docs && docs.length === 0 && (
        <div className="mt-6 rounded border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          No documents attached to this job yet.
        </div>
      )}
      {docs && docs.length > 0 && (
        <ul className="mt-4 space-y-2">
          {docs.map((d) => (
            <li key={d.id}>
              <Link
                to={`/job/${jobId}/documents/${d.id}`}
                state={{ doc: d }}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 hover:bg-gray-50"
              >
                <span
                  className={`shrink-0 rounded-full ${typeChipColor(
                    d.type,
                  )} px-2 py-0.5 text-[10px] font-bold uppercase text-white`}
                >
                  {d.type ?? 'Uncategorized'}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold">
                    {d.name ?? '(untitled)'}
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatBytes(d.size)}
                    {d.size != null ? ' · ' : ''}
                    {new Date(d.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span
                  className={`shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase ${
                    docSource(d) === 'FIELD'
                      ? 'border-indigo-500 text-indigo-600'
                      : 'border-gray-400 text-gray-600'
                  }`}
                >
                  {docSource(d)}
                </span>
                <span className="shrink-0 text-gray-400">›</span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
